import React, { useRef, useState, useEffect } from 'react';
import { useChatDock } from '../../contexts/ChatDockContext';
import { useCall } from '../../contexts/CallContext';
import supabase from '../../supabaseClient';
import { useAuth } from '../../contexts/authContext';
import { v4 as uuidv4 } from 'uuid';
import EmojiPicker from './EmojiPicker';
import GifPicker from './GifPicker';
import { FaEllipsisH, FaPaperPlane, FaReply, FaKeyboard, FaMicrophone, FaVideo, FaCheck, FaStop, FaImage, FaMusic, FaFileAlt } from 'react-icons/fa';
import AudioPlayer from './AudioPlayer';
import getMessagingErrorMessage from '../../utils/messagingErrors';

const ChatWindow = ({ contact, onClose, onMinimize }) => {
  const { messagesById, sendMessage, appendMessage, removeOptimisticMessage, deleteMessage, editMessage, addReaction, markDirectThreadReadIfNeeded, typingById, notifyTyping, onlineById, unreadCounts } = useChatDock();
  const { user } = useAuth();
  const call = useCall();
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [mode, setMode] = useState('text'); // 'text' | 'audio' | 'video'
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [reactionOpenFor, setReactionOpenFor] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const mediaRecorderRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const [pendingAudioFile, setPendingAudioFile] = useState(null);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  const videoPreviewRef = useRef(null);
  const listRef = useRef(null);
  const messages = messagesById[contact.id] || [];
  const fileInputRef = useRef(null);
  const toolsRef = useRef(null);
  const toolsToggleRef = useRef(null);
  const actionsMenuRef = useRef(null);
  const textInputRef = useRef(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const readTriggeredRef = useRef(false);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  // Reset read trigger when contact changes
  useEffect(() => {
    readTriggeredRef.current = false;
  }, [contact?.id]);

  // Strict read detection: only mark read when input focused AND scrolled near bottom
  useEffect(() => {
    if (!contact?.id || !user?.id) return;
    if (contact.type !== 'direct') return;
    if (!isInputFocused || !isNearBottom) return;
    if (readTriggeredRef.current) return;

    const hasUnreadIncoming = messages.some(m =>
      m?.sender_id === contact.id &&
      m?.receiver_id === user.id &&
      !m?.read_at
    );

    if (!hasUnreadIncoming) return;
    readTriggeredRef.current = true;
    markDirectThreadReadIfNeeded?.(contact.id);
  }, [contact?.id, contact?.type, user?.id, isInputFocused, isNearBottom, messages, markDirectThreadReadIfNeeded]);

  useEffect(() => {
    if (!showTools) return;
    const onDocClick = (e) => {
      const inPopover = toolsRef.current && toolsRef.current.contains(e.target);
      const inToggle = toolsToggleRef.current && toolsToggleRef.current.contains(e.target);
      if (!inPopover && !inToggle) {
        setShowTools(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showTools]);

  // If the user switches away from a recording mode, ensure we stop recording
  useEffect(() => {
    if (mode !== 'audio' && isRecordingAudio) {
      stopAudioRecording();
    }
    if (mode !== 'video' && isRecordingVideo) {
      stopVideoRecording();
    }
  }, [mode, isRecordingAudio, isRecordingVideo]);

  useEffect(() => {
    if (!showActionsMenu) return;
    const onDocClick = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showActionsMenu]);

  // No document-level handler needed - we use a backdrop overlay instead

  const handleSend = async () => {
    if (!text.trim()) return;
    const res = await sendMessage(contact.id, text, replyingTo?.id || null);
    if (!res?.success) {
      alert(getMessagingErrorMessage(res?.error));
      return;
    }
    setText('');
    setIsTyping(false);
    setReplyingTo(null);
  };

  const renderMedia = (m) => {
    const url = m.media_url;
    if (!url) return null;
    const urlLower = url.toLowerCase();
    const contentLower = (m.content || '').toLowerCase();
    // Check content label first for audio/video to handle .webm files correctly
    const isAud = contentLower === 'audio message' || /(\.mp3|\.wav|\.m4a|\.aac)/i.test(urlLower);
    const isVid = contentLower === 'video message' || (/(\.mp4|\.webm|\.mov|\.m4v)/i.test(urlLower) && !isAud);
    const isImg = /(\.png|\.jpg|\.jpeg|\.gif|\.webp|\.bmp|\.svg)/i.test(urlLower) || url.includes('giphy.com');
    if (isImg) return <img className="chatdock-img" src={url} alt={m.content || 'image'} />;
    if (isAud) return <AudioPlayer src={url} />;
    if (isVid) return <video className="chatdock-video" controls src={url} />;
    return <a href={url} target="_blank" rel="noreferrer">{m.content || 'Attachment'}</a>;
  };

  const formatLastSeen = (lastActive) => {
    if (!lastActive) return 'Last seen a while ago';
    const now = new Date();
    const then = new Date(lastActive);
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Last seen just now';
    if (diffMin < 60) return `Last seen ${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `Last seen ${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `Last seen ${diffDay}d ago`;
  };

  const handleEmojiSelect = (emoji) => {
    setText(prev => prev + emoji);
  };

  const handleGifSelect = async (gifUrl) => {
    if (!user?.id || !contact.id) return;
    setIsUploading(true);
    try {
      const meta = contacts.find(c => c.id === contact.id);
      const isGroup = meta?.type === 'group';
      const { error } = await supabase.from('messages').insert([{
        sender_id: user.id,
        receiver_id: isGroup ? null : contact.id,
        conversation_id: isGroup ? contact.id : null,
        media_url: gifUrl,
        content: 'GIF',
        created_at: new Date().toISOString()
      }]);
      if (error) {
        alert(getMessagingErrorMessage(error));
      }
    } catch (e) {
      alert(getMessagingErrorMessage(e));
    } finally {
      setIsUploading(false);
    }
  };

  const contacts = useChatDock().contacts || [];

  const handleFileClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      // Save picker files to attachments/ subfolder (shown in Media & Files)
      const filePath = `attachments/${user.id}/${fileName}`;
      const { error: uploadError } = await supabase
        .storage
        .from('chat-files')
        .upload(filePath, file, {
          upsert: false,
        });
      if (uploadError) throw uploadError;
      const { data: publicUrlData } = supabase.storage.from('chat-files').getPublicUrl(filePath);
      const fileUrl = publicUrlData.publicUrl;
      const meta = contacts.find(c => c.id === contact.id);
      const isGroup = meta?.type === 'group' || meta?.type === 'academy_group';
      const { error: insertError } = await supabase
        .from('messages')
        .insert([
          {
            sender_id: user.id,
            receiver_id: isGroup ? null : contact.id,
            conversation_id: isGroup ? contact.id : null,
            media_url: fileUrl,
            content: file.name,
            created_at: new Date().toISOString(),
          },
        ]);
      if (insertError) {
        alert(getMessagingErrorMessage(insertError));
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(getMessagingErrorMessage(err));
    } finally {
      setIsUploading(false);
      setUploadProgress(100);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new window.MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const file = new File([blob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
        setPendingAudioFile(file);
        stream.getTracks().forEach(t => t.stop());
        setIsRecordingAudio(false);
      };
      mediaRecorder.start();
      setIsRecordingAudio(true);
    } catch (e) {
      alert('Audio recording not available');
    }
  };

  const stopAudioRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const startVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      // Attach stream to video preview element
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }
      const mediaRecorder = new window.MediaRecorder(stream);
      videoRecorderRef.current = mediaRecorder;
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const file = new File([blob], `video_${Date.now()}.webm`, { type: 'video/webm' });
        setPendingVideoFile(file);
        stream.getTracks().forEach(t => t.stop());
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = null;
        }
        setIsRecordingVideo(false);
      };
      mediaRecorder.start();
      setIsRecordingVideo(true);
    } catch (e) {
      alert('Video recording not available');
    }
  };

  const stopVideoRecording = () => {
    videoRecorderRef.current?.stop();
  };

  const uploadBlob = async (file, label) => {
    if (!user?.id) return;
    setIsUploading(true);
    let optimisticMsg = null;
    try {
      const fileExt = (file.name.split('.').pop()) || 'webm';
      const fileName = `${uuidv4()}.${fileExt}`;
      // Save composer audio/video to messages/ subfolder (not shown in Media & Files)
      const filePath = `messages/${user.id}/${fileName}`;
      const { error } = await supabase.storage.from('chat-files').upload(filePath, file);
      if (error) throw error;
      const { data: publicUrlData } = supabase.storage.from('chat-files').getPublicUrl(filePath);
      const fileUrl = publicUrlData.publicUrl;
      const meta = contacts.find(c => c.id === contact.id);
      const isGroup = meta?.type === 'group' || meta?.type === 'academy_group';
      const created_at = new Date().toISOString();
      const msgPayload = {
        sender_id: user.id,
        receiver_id: isGroup ? null : contact.id,
        conversation_id: isGroup ? contact.id : null,
        media_url: fileUrl,
        content: label || file.name,
        created_at,
      };
      // Optimistic UI: append message immediately so it shows without waiting for realtime
      appendMessage(contact.id, msgPayload);
      optimisticMsg = msgPayload;
      const { error: insertError } = await supabase.from('messages').insert([msgPayload]);
      if (insertError) {
        removeOptimisticMessage?.(contact.id, optimisticMsg);
        alert(getMessagingErrorMessage(insertError));
      }
    } catch (e) {
      if (optimisticMsg) {
        removeOptimisticMessage?.(contact.id, optimisticMsg);
      }
      alert(getMessagingErrorMessage(e));
    } finally {
      setIsUploading(false);
      setUploadProgress(100);
    }
  };

  const handleSendAudioRecording = async () => {
    if (!pendingAudioFile) return;
    await uploadBlob(pendingAudioFile, 'Audio message');
    setPendingAudioFile(null);
  };

  const handleClearAudioRecording = () => {
    if (isRecordingAudio) {
      stopAudioRecording();
    }
    setPendingAudioFile(null);
  };

  const handleSendVideoRecording = async () => {
    if (!pendingVideoFile) return;
    await uploadBlob(pendingVideoFile, 'Video message');
    setPendingVideoFile(null);
  };

  const handleClearVideoRecording = () => {
    if (isRecordingVideo) {
      stopVideoRecording();
    }
    setPendingVideoFile(null);
  };

  const renderModeIcon = () => {
    if (mode === 'audio') return <FaMicrophone size={12} color="#e2e8f0" />;
    if (mode === 'video') return <FaVideo size={12} color="#e2e8f0" />;
    return <FaKeyboard size={12} color="#e2e8f0" />;
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    await deleteMessage(contact.id, messageId);
  };

  const handleStartEdit = (m) => {
    if (m.media_url) return; // Can't edit media messages
    setEditingMessageId(m.id);
    setEditText(m.content || '');
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId) return;
    await editMessage(contact.id, editingMessageId, editText);
    setEditingMessageId(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
  };

  return (
    <div className="chatdock-window">
      <div className="chatdock-window-header">
        <div className="chatdock-window-title">
          <span className="chatdock-title-actions" ref={actionsMenuRef}>
            <button className="chatdock-chevron-btn" onClick={() => setShowActionsMenu(v => !v)} title="Actions">▾</button>
            {showActionsMenu && (
              <div className="chatdock-actions-menu">
                <button
                  className="chatdock-menu-item"
                  onClick={() => {
                    window.open(
                      contact.type === 'direct'
                        ? `/app/profile/${contact.id}`
                        : `/app/groups/${contact.id}`,
                      '_blank',
                      'noopener',
                    );
                    setShowActionsMenu(false);
                  }}
                >
                  <span>👤</span> {contact.type === 'direct' ? 'View profile' : 'View group'}
                </button>
                <button
                  className="chatdock-menu-item"
                  onClick={() => {
                    window.dispatchEvent(
                      new CustomEvent('virtulingu:openMessenger', {
                        detail: { contactId: contact.id },
                      }),
                    );
                    setShowActionsMenu(false);
                  }}
                >
                  <span>💬</span> Open in Messenger
                </button>
              </div>
            )}
          </span>
          {contact.type === 'group' && contact.group_banner_url ? (
            <img src={contact.group_banner_url} alt="" className="chatdock-avatar-click" onClick={onMinimize} title="Minimize" />
          ) : contact.avatar_url ? (
            <img src={contact.avatar_url} alt="" className="chatdock-avatar-click" onClick={onMinimize} title="Minimize" />
          ) : (
            <div className="chatdock-avatar-fallback chatdock-avatar-click" onClick={onMinimize} title="Minimize">{(contact.name||'U').charAt(0)}</div>
          )}
          <div className="chatdock-title-col">
            <div className="chatdock-title-name-row">
              <span className="chatdock-title-name" title={contact.name}>{contact.name}</span>
            </div>
            {contact.type === 'direct' && !onlineById?.[contact.id] && contact.last_active && (
              <span className="chatdock-last-seen">{formatLastSeen(contact.last_active)}</span>
            )}
            {(typingById[contact.id] || isTyping) && <span className="chatdock-typing">typing…</span>}
          </div>
          {contact.type === 'direct' && onlineById?.[contact.id] && <span className="chatdock-online dot" title="Online" />}
        </div>
        <div className="chatdock-window-actions">
          {!!unreadCounts?.[contact.id] && <span className="chatdock-title-badge">{unreadCounts[contact.id] > 9 ? '9+' : unreadCounts[contact.id]}</span>}
          
          {/* Join Call button - shows when there's an active call in this thread */}
          {call?.activeCallsByThread?.[contact.id] && (
            <button
              className="cd-action cd-join-call"
              onClick={() => {
                const activeCall = call.activeCallsByThread[contact.id];
                if (activeCall && call?.joinCall) {
                  call.joinCall({
                    callId: activeCall.callId,
                    roomId: activeCall.roomId,
                    mode: activeCall.mode,
                    thread: {
                      id: contact.id,
                      type: contact.type || 'group',
                      name: contact.name,
                    },
                  });
                }
              }}
              title="Join ongoing call"
            >
              🔴 Join
            </button>
          )}
          
          {/* Call buttons - hide if there's already an active call */}
          {!call?.activeCallsByThread?.[contact.id] && (
            <>
              <button
                className="cd-action cd-call"
                onClick={() => {
                  if (call?.startCall) {
                    call.startCall({
                      thread: {
                        id: contact.id,
                        type: contact.type || 'direct',
                        name: contact.name || contact.email,
                        avatar: contact.avatar_url || contact.group_banner_url,
                      },
                      mode: 'audio',
                    });
                  }
                }}
                title="Audio Call"
              >
                📞
              </button>
              <button
                className="cd-action cd-call"
                onClick={() => {
                  if (call?.startCall) {
                    call.startCall({
                      thread: {
                        id: contact.id,
                        type: contact.type || 'direct',
                        name: contact.name || contact.email,
                        avatar: contact.avatar_url || contact.group_banner_url,
                      },
                      mode: 'video',
                    });
                  }
                }}
                title="Video Call"
              >
                🎥
              </button>
            </>
          )}
          {/* Rightmost last: close, then leftwards minimize */}
          <button className="cd-action" onClick={onMinimize} title="Minimize">_</button>
          <button className="cd-action" onClick={onClose} title="Close">×</button>
        </div>
        {/* actions menu is rendered inside .chatdock-title-actions */}
        <input ref={fileInputRef} type="file" id="chatdock-image-upload" accept="image/*,.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg" style={{ display: 'none' }} onChange={handleFileChange} />
        <input type="file" id="chatdock-audio-upload" accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac" style={{ display: 'none' }} onChange={handleFileChange} />
        <input type="file" id="chatdock-video-upload" accept="video/*,.mp4,.mov,.avi,.wmv,.flv,.mkv" style={{ display: 'none' }} onChange={handleFileChange} />
        <input type="file" id="chatdock-doc-upload" accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.rar" style={{ display: 'none' }} onChange={handleFileChange} />
      </div>
      <div
        className="chatdock-window-body"
        ref={listRef}
        onClick={() => setMenuOpenFor(null)}
        onScroll={() => {
          const el = listRef.current;
          if (!el) return;
          const threshold = 80;
          const near = (el.scrollHeight - (el.scrollTop + el.clientHeight)) < threshold;
          setIsNearBottom(near);
        }}
      >
        {messages.map((m) => {
          const isMine = m.sender_id === user?.id;
          const isEditing = editingMessageId === m.id;
          const isMenuOpen = menuOpenFor === m.id;
          // Message status: sent (has id), delivered (delivered_at), read (read_at)
          const getStatusIcon = () => {
            if (!isMine) return null;
            if (m.read_at) return <span className="chatdock-status read" title="Read">✓✓</span>;
            if (m.delivered_at) return <span className="chatdock-status delivered" title="Delivered">✓✓</span>;
            if (m.id) return <span className="chatdock-status sent" title="Sent">✓</span>;
            return <span className="chatdock-status pending" title="Sending...">○</span>;
          };
          return (
            <div key={m.id || m.created_at+Math.random()} id={m.id ? `msg-${m.id}` : undefined} className={`chatdock-msg-row ${isMine ? 'own' : 'other'}`}>
              <div className="chatdock-msg-wrapper">
                <div className={`chatdock-msg-bubble ${isMine ? 'mine' : 'their'}`}>
                {/* Ellipsis menu at top-right corner for own messages */}
                {isMine && m.id && !isEditing && (
                  <span className="chatdock-msg-menu" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="chatdock-msg-ellipsis"
                      onClick={() => setMenuOpenFor(isMenuOpen ? null : m.id)}
                      title="More options"
                    >
                      ⋯
                    </button>
                    {isMenuOpen && (
                      <div className="chatdock-msg-dropdown">
                        {!m.media_url && (
                          <button onClick={() => { setMenuOpenFor(null); handleStartEdit(m); }}>
                            ✎ Edit
                          </button>
                        )}
                        <button className="delete" onClick={() => { setMenuOpenFor(null); handleDeleteMessage(m.id); }}>
                          🗑 Delete
                        </button>
                      </div>
                    )}
                  </span>
                )}
                {isEditing ? (
                  <div className="chatdock-edit-form">
                    <input
                      type="text"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                      autoFocus
                    />
                    <div className="chatdock-edit-actions">
                      <button onClick={handleSaveEdit} title="Save">✓</button>
                      <button onClick={handleCancelEdit} title="Cancel">✕</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Reply reference if this message is a reply */}
                    {m.reply_to_id && (() => {
                      const replyMsg = messages.find(msg => msg.id === m.reply_to_id);
                      return replyMsg ? (
                        <div className="chatdock-reply-ref" onClick={() => {
                          const el = document.getElementById(`msg-${m.reply_to_id}`);
                          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }}>
                          ↩ {replyMsg.content?.slice(0, 40)}{replyMsg.content?.length > 40 ? '…' : ''}
                        </div>
                      ) : null;
                    })()}
                    {m.media_url ? renderMedia(m) : (<span className="chatdock-msg-text">{m.content}</span>)}
                    {m.edited_at && <span className="chatdock-edited">(edited)</span>}
                  </>
                )}
                <div className="chatdock-msg-footer">
                  <span className="chatdock-ts" title={m.created_at}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  {getStatusIcon()}
                </div>
                {/* Display reactions on message bubble */}
                {m.message_reactions?.length > 0 && (
                  <div className="chatdock-msg-reactions">
                    {m.message_reactions.map((r) => (
                      <span key={r.id} className="chatdock-reaction-badge" title={`Reaction by user`}>
                        {r.emoji}
                      </span>
                    ))}
                  </div>
                )}
              </div>
                {/* Quick actions: React + Reply icons (Facebook-style) */}
                <div className="chatdock-quick-actions">
                  <button
                    className="chatdock-quick-action"
                    title="React"
                    onClick={(e) => { e.stopPropagation(); setReactionOpenFor(reactionOpenFor === m.id ? null : m.id); }}
                  >
                    😊
                  </button>
                  <button
                    className="chatdock-quick-action"
                    title="Reply"
                    onClick={(e) => { e.stopPropagation(); setReplyingTo(m); }}
                  >
                    ↩
                  </button>
                </div>
                {/* Reactions strip - only shown when clicking React icon */}
                {reactionOpenFor === m.id && (
                  <>
                    {/* Invisible backdrop to close popup on outside click */}
                    <div
                      style={{ position: 'fixed', inset: 0, zIndex: 99999 }}
                      onClick={() => setReactionOpenFor(null)}
                    />
                    <div
                      className="chatdock-reactions-popup"
                      style={{ position: 'absolute', zIndex: 100001 }}
                    >
                      {['👍', '😊', '❤️', '😮', '😢', '👏'].map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          className="chatdock-reaction-btn"
                          style={{ position: 'relative', zIndex: 100002, cursor: 'pointer' }}
                          title={`React with ${emoji}`}
                          onClick={() => {
                            console.debug('[ChatDock] reaction click', { messageId: m.id, emoji });
                            setReactionOpenFor(null);
                            addReaction?.(m.id, emoji);
                          }}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="chatdock-window-input">
        {/* Reply preview bar */}
        {replyingTo && (
          <div className="chatdock-reply-preview">
            <div className="chatdock-reply-info">
              <span className="chatdock-reply-label">Replying to</span>
              <span className="chatdock-reply-text">{replyingTo.content?.slice(0, 50)}{replyingTo.content?.length > 50 ? '…' : ''}</span>
            </div>
            <button className="chatdock-reply-cancel" onClick={() => setReplyingTo(null)} title="Cancel reply">×</button>
          </div>
        )}
        <div className="chatdock-window-input-row">
        <div className="chatdock-toolbar">
          <button ref={toolsToggleRef} className="cd-tool cd-more" title="More tools" onClick={() => setShowTools(v => !v)}>
            {showTools ? '▴' : '▾'}
          </button>
          {showTools && (
            <div ref={toolsRef} className="chatdock-tools-popover">
              <label htmlFor="chatdock-image-upload" className="cd-tool" title="Send/attach image" onClick={() => setShowTools(false)}>
                <FaImage />
              </label>
              <label htmlFor="chatdock-audio-upload" className="cd-tool" title="Send/attach audio file" onClick={() => setShowTools(false)}>
                <FaMusic />
              </label>
              <label htmlFor="chatdock-video-upload" className="cd-tool" title="Send/attach video file" onClick={() => setShowTools(false)}>
                <FaVideo />
              </label>
              <label htmlFor="chatdock-doc-upload" className="cd-tool" title="Send/attach document" onClick={() => setShowTools(false)}>
                <FaFileAlt />
              </label>
              <button className="cd-tool" title="GIF" onClick={() => { setShowTools(false); setShowGifPicker(!showGifPicker); setShowEmojiPicker(false); }}>GIF</button>
              <button className="cd-tool" title="Emoji" onClick={() => { setShowTools(false); setShowEmojiPicker(!showEmojiPicker); setShowGifPicker(false); }}>😀</button>
            </div>
          )}
        </div>
        <div className="chatdock-composer">
          <div className="chatdock-mode-toggle" aria-label="Message input mode">
            <button
              type="button"
              className={`chatdock-mode-pill ${mode}`}
              onClick={() => setShowModeMenu(v => !v)}
              aria-haspopup="true"
              aria-expanded={showModeMenu}
              title="Select message type"
            >
              <span className="chatdock-mode-icon">{renderModeIcon()}</span>
              <span className="chatdock-mode-caret">▾</span>
            </button>
            {showModeMenu && (
              <div className="chatdock-mode-menu">
                <button type="button" className={`chatdock-mode-menu-item ${mode === 'text' ? 'active' : ''}`} onClick={() => { setMode('text'); setShowModeMenu(false); }} aria-label="Send Text Message" title="Send Text Message"><span className="chatdock-mode-icon"><FaKeyboard size={12} color="#e2e8f0" /></span></button>
                <button type="button" className={`chatdock-mode-menu-item ${mode === 'audio' ? 'active' : ''}`} onClick={() => { setMode('audio'); setShowModeMenu(false); }} aria-label="Record Audio Message" title="Record Audio Message"><span className="chatdock-mode-icon"><FaMicrophone size={12} color="#e2e8f0" /></span></button>
                <button type="button" className={`chatdock-mode-menu-item ${mode === 'video' ? 'active' : ''}`} onClick={() => { setMode('video'); setShowModeMenu(false); }} aria-label="Record Video Message" title="Record Video Message"><span className="chatdock-mode-icon"><FaVideo size={12} color="#e2e8f0" /></span></button>
              </div>
            )}
          </div>

          {mode === 'text' && (
            <>
              <input
                ref={textInputRef}
                className="chatdock-input"
                value={text}
                onChange={(e) => { setText(e.target.value); setIsTyping(e.target.value.length > 0); notifyTyping(contact.id); }}
                placeholder="Aa"
                onKeyDown={(e) => { if (e.key === 'Enter') { handleSend(); } }}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
              />
              <button type="button" className="chatdock-clear" onClick={() => setText('')} disabled={!text} title="Clear">
                ×
              </button>
              <button
                type="button"
                className="chatdock-send"
                onClick={handleSend}
                aria-label="Send Message" title="Send Message"
              >
                <span className="chatdock-send-icon"><FaPaperPlane size={14} color="#fff" /></span>
              </button>
            </>
          )}

          {mode === 'audio' && (
            <>
              <div className="chatdock-record-row chatdock-record-audio">
                {/* Recording indicator or ready-to-send cue */}
                {isRecordingAudio ? (
                  <div className="chatdock-record-visual active">
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                  </div>
                ) : pendingAudioFile ? (
                  <div className="chatdock-ready-cue">
                    <FaCheck size={12} color="#22c55e" />
                    <span>Ready</span>
                  </div>
                ) : (
                  <div className="chatdock-record-visual">
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                  </div>
                )}
                <button
                  type="button"
                  className={`chatdock-record-btn ${isRecordingAudio ? 'recording' : ''}`}
                  onClick={() => { if (isRecordingAudio) { stopAudioRecording(); } else { startAudioRecording(); } }}
                  disabled={isRecordingVideo}
                  title={isRecordingAudio ? 'Stop Recording' : 'Record Audio'}
                >
                  <span className="chatdock-record-icon">{isRecordingAudio ? <FaStop size={12} color="#fff" /> : <FaMicrophone size={12} color="#fff" />}</span>
                </button>
              </div>
              <button type="button" className="chatdock-clear" onClick={handleClearAudioRecording} disabled={!isRecordingAudio && !pendingAudioFile || isUploading} title="Clear">
                ×
              </button>
              <button
                type="button"
                className={`chatdock-send ${pendingAudioFile ? 'ready' : ''}`}
                onClick={handleSendAudioRecording}
                disabled={isRecordingAudio || !pendingAudioFile || isUploading}
                aria-label="Send Message" title="Send Message"
              >
                <span className="chatdock-send-icon"><FaPaperPlane size={14} color="#fff" /></span>
              </button>
            </>
          )}

          {mode === 'video' && (
            <>
              <div className="chatdock-record-row chatdock-record-video">
                {/* Video preview while recording, or ready-to-send cue */}
                {isRecordingVideo ? (
                  <video
                    ref={(el) => {
                      videoPreviewRef.current = el;
                      if (el && streamRef.current && el.srcObject !== streamRef.current) {
                        el.srcObject = streamRef.current;
                      }
                    }}
                    autoPlay
                    muted
                    playsInline
                    className="chatdock-video-preview"
                  />
                ) : pendingVideoFile ? (
                  <div className="chatdock-ready-cue">
                    <FaCheck size={12} color="#22c55e" />
                    <span>Ready</span>
                  </div>
                ) : (
                  <div className="chatdock-record-visual">
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                  </div>
                )}
                <button
                  type="button"
                  className={`chatdock-record-btn ${isRecordingVideo ? 'recording' : ''}`}
                  onClick={() => { if (isRecordingVideo) { stopVideoRecording(); } else { startVideoRecording(); } }}
                  disabled={isRecordingAudio}
                  title={isRecordingVideo ? 'Stop Recording' : 'Record Video'}
                >
                  <span className="chatdock-record-icon">{isRecordingVideo ? <FaStop size={12} color="#fff" /> : <FaVideo size={12} color="#fff" />}</span>
                </button>
              </div>
              <button type="button" className="chatdock-clear" onClick={handleClearVideoRecording} disabled={!isRecordingVideo && !pendingVideoFile || isUploading} title="Clear">
                ×
              </button>
              <button
                type="button"
                className={`chatdock-send ${pendingVideoFile ? 'ready' : ''}`}
                onClick={handleSendVideoRecording}
                disabled={isRecordingVideo || !pendingVideoFile || isUploading}
                aria-label="Send Message" title="Send Message"
              >
                <span className="chatdock-send-icon"><FaPaperPlane size={14} color="#fff" /></span>
              </button>
            </>
          )}
        </div>
        </div>

        {showEmojiPicker && <EmojiPicker onSelect={handleEmojiSelect} onClose={() => setShowEmojiPicker(false)} />}
        {showGifPicker && <GifPicker onSelect={handleGifSelect} onClose={() => setShowGifPicker(false)} />}
      </div>
      {isUploading && <div className="chatdock-upload">Uploading… {uploadProgress}%</div>}
    </div>
  );
};

export default ChatWindow;
